import type { Agent, ClinicReport, DailyStats, ErrorLogEntry, ProtocolStep, ServiceHealth, Stage } from '../types';
import { agents, babies, calls, clinicProtocols, dailyStats, defaultProtocol, doses, errorLog, facilities, flags, mothers, serviceHealth, topQuestions, withAbout } from './mockData';
import { addAudit, type Actor } from './auditService';
import { respond } from './respond';
import { collection, doc, getDoc, getDocs, setDoc, writeBatch } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { isReal, loadClinic, loadDailyStats } from './store';
import { scope } from './scope';
import { computeImpact } from './impact';
import { callServer } from './server';

// Default vaccine schedule (platform/defaultProtocol), real mode.
async function realDefault(): Promise<ProtocolStep[]> {
  const snap = await getDoc(doc(db!, 'platform', 'defaultProtocol'));
  return (snap.exists() ? (snap.data().steps as ProtocolStep[]) : defaultProtocol).map(withAbout);
}
async function realClinicSteps(fid: string): Promise<ProtocolStep[]> {
  const snap = await getDocs(collection(db!, 'facilities', fid, 'protocols'));
  return snap.docs.map((d) => withAbout({ ...(d.data() as ProtocolStep), id: d.id }));
}
// Copy the default schedule into a new clinic (super admin, at clinic creation).
export async function copyDefaultToReal(fid: string): Promise<void> {
  const steps = await realDefault();
  const b = writeBatch(db!);
  steps.forEach((s) => b.set(doc(db!, 'facilities', fid, 'protocols', s.id), { ...s, facilityId: fid }));
  await b.commit();
}

export const CORE_RULES = { version: 'core-1.4', keys: ['identity', 'consent', 'optout', 'noAdvice', 'escalate', 'noSex', 'hours', 'family'] };

export async function getSystemHealth(): Promise<{ services: ServiceHealth[]; errors: ErrorLogEntry[] }> {
  return respond({ services: serviceHealth, errors: [...errorLog].sort((a, b) => b.at.localeCompare(a.at)) });
}

export async function getAgentsLibrary(): Promise<Agent[]> {
  return respond(agents);
}

const copy = (steps: ProtocolStep[], facilityId: string) => steps.map((s) => ({ ...s, facilityId }));

export async function getDefaultProtocol(stage: Stage = 'baby'): Promise<ProtocolStep[]> {
  if (isReal()) return (await realDefault()).filter((s) => s.stage === stage).map((s) => ({ ...s }));
  return respond(defaultProtocol.filter((s) => s.stage === stage).map((s) => ({ ...s })));
}

export async function saveDefaultProtocol(rows: ProtocolStep[], actor?: Actor, stage: Stage = 'baby'): Promise<void> {
  const label = stage === 'baby' ? 'Default vaccine schedule' : 'Default pregnancy schedule';
  if (isReal()) {
    const keep = (await realDefault()).filter((s) => s.stage !== stage);
    await setDoc(doc(db!, 'platform', 'defaultProtocol'), { steps: JSON.parse(JSON.stringify([...copy(rows, 'default'), ...keep])), updatedAt: new Date().toISOString() });
    addAudit(actor, 'protocol.updated', label, undefined, `${rows.length} steps`);
    return;
  }
  const keep = defaultProtocol.filter((s) => s.stage !== stage);
  defaultProtocol.splice(0, defaultProtocol.length, ...copy(rows, 'default'), ...keep);
  addAudit(actor, 'protocol.updated', label, undefined, `${rows.length} steps`);
  return respond(undefined);
}

export function copyDefaultTo(facilityId: string): void {
  clinicProtocols[facilityId] = copy(defaultProtocol, facilityId);
}

export async function getClinicSchedule(facilityId: string): Promise<ProtocolStep[]> {
  if (isReal()) {
    const steps = await realClinicSteps(facilityId);
    return steps.length ? steps : copy(await realDefault(), facilityId);
  }
  if (!clinicProtocols[facilityId]) copyDefaultTo(facilityId);
  return respond(clinicProtocols[facilityId].map((s) => ({ ...s })));
}

export async function saveClinicSchedule(facilityId: string, stage: Stage, rows: ProtocolStep[], actor?: Actor): Promise<void> {
  if (isReal()) {
    // Replace this stage's steps; keep the other stage. New patients get the new schedule.
    const current = await realClinicSteps(facilityId);
    const others = current.length ? current.filter((s) => s.stage !== stage) : copy(await realDefault(), facilityId).filter((s) => s.stage !== stage);
    const b = writeBatch(db!);
    current.forEach((s) => b.delete(doc(db!, 'facilities', facilityId, 'protocols', s.id)));
    [...copy(rows, facilityId), ...others].forEach((s) => b.set(doc(db!, 'facilities', facilityId, 'protocols', s.id), JSON.parse(JSON.stringify(s))));
    await b.commit();
    addAudit(actor, 'schedule.updated', facilityId, facilityId, `${stage}: ${rows.length} items`);
    return;
  }
  if (!clinicProtocols[facilityId]) copyDefaultTo(facilityId);
  const keep = clinicProtocols[facilityId].filter((s) => s.stage !== stage);
  clinicProtocols[facilityId] = [...copy(rows, facilityId), ...keep];
  addAudit(actor, 'schedule.updated', facilities.find((f) => f.id === facilityId)?.displayNameEn ?? facilityId, facilityId, `${stage}: ${rows.length} items`);
  return respond(undefined);
}

// Aggregates only: no patient fields leave this function.
export async function getClinicReport(facilityId: string): Promise<ClinicReport> {
  if (isReal()) {
    const rows = await loadDailyStats(facilityId, 90);
    const last = rows[rows.length - 1];
    // Clinic staff count their own flags; super admin only ever sees the daily counts.
    const fl = scope().role === 'super_admin' ? null : (await loadClinic(facilityId)).flags;
    const flagCounts = fl
      ? { open: fl.filter((x) => x.status === 'open').length, inProgress: fl.filter((x) => x.status === 'in_progress').length, resolved: fl.filter((x) => x.status === 'resolved').length, urgent: fl.filter((x) => x.urgency === 'urgent').length }
      : { open: last?.openEscalations ?? 0, inProgress: last?.flagsInProgress ?? 0, resolved: last?.flagsResolved ?? 0, urgent: last?.flagsUrgent ?? 0 };
    const fac = await getDoc(doc(db!, 'facilities', facilityId));
    // Impact: clinic staff work it out from their own records; super admin gets the nightly totals (counts only).
    const impact = scope().role === 'super_admin' ? last?.impact : computeImpact(await loadClinic(facilityId));
    return { ...report(rows, fac.data()?.baselineOnTimeRate as number | undefined, [], flagCounts), impact };
  }
  const rows = dailyStats.filter((s) => s.facilityId === facilityId);
  const f = facilities.find((x) => x.id === facilityId);
  const fl = flags.filter((x) => x.facilityId === facilityId);
  const mine = <T extends { facilityId: string }>(xs: T[]) => xs.filter((x) => x.facilityId === facilityId);
  const impact = computeImpact({ mothers: mine(mothers), babies: mine(babies), doses: mine(doses), calls: mine(calls), flags: fl });
  return respond({ ...report(rows, f?.baselineOnTimeRate, topQuestions[facilityId] ?? [], {
    open: fl.filter((x) => x.status === 'open').length,
    inProgress: fl.filter((x) => x.status === 'in_progress').length,
    resolved: fl.filter((x) => x.status === 'resolved').length,
    urgent: fl.filter((x) => x.urgency === 'urgent').length,
  }), impact });
}

function report(rows: DailyStats[], baseline: number | undefined, topQ: ClinicReport['topQuestions'], flagCounts: ClinicReport['flags']): ClinicReport {
  const sum = (r: typeof rows, k: 'callsMade' | 'callsAnswered') => r.reduce((a, x) => a + x[k], 0);
  const rate = (r: typeof rows) => (sum(r, 'callsMade') ? Math.round((sum(r, 'callsAnswered') / sum(r, 'callsMade')) * 100) : 0);
  const start = Math.max(0, rows.length - 28);
  const weeklyAnswer = [0, 1, 2, 3].map((w) => ({ label: `W${w + 1}`, rate: rate(rows.slice(start + w * 7, start + w * 7 + 7)) }));
  const last = rows[rows.length - 1]?.onTimeRate ?? 100;
  return {
    onTime: rows.map((r) => ({ date: r.date, rate: r.onTimeRate })),
    baseline,
    avgDaysLate: rows.length ? Math.round(((100 - last) / 8) * 10) / 10 : 0,
    answerRate: rate(rows),
    weeklyAnswer,
    topQuestions: topQ,
    flags: flagCounts,
    rows,
  };
}

/** Webhook safety log (super admin): rejected call-result messages per day and reason. Counts only, no patient data. */
export interface WebhookDay { day: string; counts: Record<string, number> }
export const WEBHOOK_REASONS = ['bad_signature', 'too_late', 'unknown_call', 'not_json', 'not_post', 'duplicate'] as const;

export async function getWebhookStats(days = 7): Promise<WebhookDay[]> {
  if (isReal()) {
    const snap = await getDocs(collection(db!, 'webhookStats'));
    return snap.docs
      .map((d) => ({ day: d.id, counts: ((d.data() as { sarvam?: Record<string, number> }).sarvam ?? {}) }))
      .sort((a, b) => b.day.localeCompare(a.day)).slice(0, days);
  }
  // Demo: one probe rejected yesterday, so the panel shows how it looks.
  const y = new Date(Date.now() - 864e5).toISOString().slice(0, 10);
  return respond<WebhookDay[]>([{ day: new Date().toISOString().slice(0, 10), counts: {} }, { day: y, counts: { bad_signature: 1, total: 1 } }]);
}

/** System health "Last backup" (super admin). Backup metadata only, never patient data. */
export interface BackupStatus { latestAt: string | null; count: number; health: 'ok' | 'stale' | 'none'; pitr: boolean }
export async function getBackupStatus(): Promise<BackupStatus> {
  if (isReal()) return callServer<BackupStatus>('getBackupStatus', {});
  // Demo: last night's backup at 02:10 India time.
  const d = new Date(); d.setUTCHours(20, 40, 0, 0); if (d.getTime() > Date.now()) d.setUTCDate(d.getUTCDate() - 1);
  return respond({ latestAt: d.toISOString(), count: 30, health: 'ok', pitr: true });
}
